import { EditOutlined } from '@ant-design/icons'
import { Button, Divider, Flex, Table, TableColumnsType } from 'antd'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useClassifierServiceGetClassifierListEndpoint } from 'src/api/queries'
import { ClassifierCategory, ClassifierType } from 'src/api/requests'
import { SearchBar, Paragraph } from 'src/components'
import { CategorySelect } from 'src/features/classifiers'
import { useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { resetPagination } from 'src/utils'

const restrictedClassifierTypes = [
  ClassifierType.COUNTRIES,
  ClassifierType.CUATM,
  ClassifierType.IC_D10AM,
  ClassifierType.DEVICE_TYPE,
  ClassifierType.INVESTIGATION_RESULT_VALUE_OPTION,
  ClassifierType.REFERENCE_INTERVAL,
  ClassifierType.INVESTIGATION_TEST_TYPE,
  ClassifierType.INVESTIGATION_TYPE,
  ClassifierType.MEDICAL_INSTITUTION
]

export const ClassifiersTable = () => {
  const { t } = useLocales()
  const navigate = useNavigate()
  const pagination = useTablePagination()

  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<ClassifierCategory>()

  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize),
    ...(!!search && {
      searchTerm: search
    }),
    ...(!!category && {
      category
    })
  }
  const { data, isLoading } =
    useClassifierServiceGetClassifierListEndpoint(queryParams)

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'title',
        title: t('classifiers.title'),
        dataIndex: 'name',
        width: '25%'
      },
      {
        key: 'description',
        title: t('classifiers.description'),
        render: ({ description }) => (
          <Paragraph weight={400} ellipsis={{ rows: 2 }}>
            {description}
          </Paragraph>
        ),

        width: '55%'
      },
      {
        key: 'category',
        title: t('classifiers.category'),
        dataIndex: 'category',
        width: '20%'
      },
      {
        key: 'actions',
        render: ({ id }) => (
          <Button
            icon={<EditOutlined />}
            href={paths.classifiers.details(id)}
          />
        ),
        width: '10%'
      }
    ],
    [t]
  )

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const onCategorySelect = (val: ClassifierCategory) => {
    setCategory(val)
    resetPagination(pagination)
  }

  const dataSource = useMemo(
    () =>
      data?.data?.filter(
        (item) => item.type && !restrictedClassifierTypes.includes(item.type)
      ),
    [data]
  )

  return (
    <Flex vertical>
      <Flex justify="space-between">
        <SearchBar
          onSearch={onSearch}
          placeholder={t('classifiers.search_placeholder')}
        />

        <Flex gap={16} align="center">
          {/* ICD-10-AM stays out of the generic editor; only its fișa 058-1/e list is edited. */}
          <Button
            onClick={() => navigate(paths.classifiers.opportunisticDiseases)}
          >
            {t('classifiers.opportunistic_diseases')}
          </Button>

          <CategorySelect
            onChange={(category: ClassifierCategory) =>
              onCategorySelect(category)
            }
          />
        </Flex>
      </Flex>

      <Divider />

      <Table
        bordered
        rowKey="id"
        columns={columns}
        loading={isLoading}
        dataSource={dataSource}
        scroll={{ x: 'max-content' }}
        pagination={{
          ...pagination,
          total: data?.totalCount
        }}
      />
    </Flex>
  )
}
